'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { Toaster } from 'sonner';
import { QueryProvider } from './query-provider';

// Wrapper popover pour le Toaster.
//
// Contexte : nos modales utilisent l'élément `<dialog>` HTML natif avec
// `showModal()`, qui met le contenu dans le **top-layer** du browser. Tout
// élément hors top-layer (même avec z-index 2147483647) reste en dessous.
// Pour exposer un toast par-dessus une modale ouverte, le Toaster doit
// lui aussi être dans le top-layer, et y entrer **après** la modale —
// car les éléments du top-layer sont stackés dans l'ordre d'ouverture.
//
// Stratégie :
//   1. Le Toaster vit dans un `<div popover="manual">` placé fixed sur le
//      viewport. On l'ouvre une première fois au mount.
//   2. À chaque ouverture d'un `<dialog>` (open=true), on hide+showPopover
//      sur le host pour le remettre au sommet du top-layer. Ainsi, tout
//      toast déclenché pendant la modale s'affiche AU-DESSUS d'elle.
//   3. Fallback : si l'API Popover n'est pas dispo (browsers très anciens),
//      le Toaster reste rendu en flow normal (pas au-dessus de la modale,
//      mais visible quand aucune modale n'est ouverte).

// Onboarding : les actions d'étape flottent en haut à droite (FloatingStepActions,
// top 16 px + bouton lg 44 px). Les toasts descendent sous elles au lieu de les
// recouvrir après un dépôt de document.
const ONBOARDING_TOAST_TOP_OFFSET = 76;

function ToasterTopLayer() {
  const hostRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const onboarding = pathname?.startsWith('/onboarding') ?? false;

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const supportsPopover = typeof el.showPopover === 'function';
    if (!supportsPopover) return;

    const bump = () => {
      try {
        if (el.matches(':popover-open')) el.hidePopover();
        el.showPopover();
      } catch {
        // Tolère les transitions impossibles (popover déjà fermé/ouvert
        // par le browser, navigation, etc.).
      }
    };

    bump();

    // Observe les ouvertures de dialogs n'importe où dans le document.
    // Couvre les 2 chemins :
    //   • un `<dialog>` déjà présent dont l'attribut `open` passe à true ;
    //   • un nouveau `<dialog open>` ajouté au DOM.
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'open') {
          const target = m.target as HTMLElement;
          if (target.tagName === 'DIALOG' && target.hasAttribute('open')) {
            bump();
            return;
          }
        }
        if (m.type === 'childList') {
          for (const node of Array.from(m.addedNodes)) {
            if (
              node instanceof HTMLElement &&
              node.tagName === 'DIALOG' &&
              node.hasAttribute('open')
            ) {
              bump();
              return;
            }
          }
        }
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={hostRef}
      popover="manual"
      className="pointer-events-none fixed inset-0 m-0 border-0 bg-transparent p-0"
    >
      {/* `pointer-events-auto` sur CHAQUE toast : le host popover est en
          `pointer-events-none` (il couvre tout l'écran, les clics doivent le
          traverser) et sonner ne réactive jamais les événements sur ses toasts
          — sans ça, rien n'est cliquable dedans (liens, actions, bouton fermer). */}
      {/* `expand` : les toasts simultanés s'empilent DÉPLIÉS (chacun lisible) au
          lieu du deck replié qui masque le texte des toasts derrière — cas envoi
          email : succès + pill « temps gagné » 900 ms après (retour Max 25/07). */}
      <Toaster
        position="top-right"
        richColors
        closeButton
        expand
        offset={onboarding ? { top: ONBOARDING_TOAST_TOP_OFFSET } : undefined}
        toastOptions={{ className: 'pointer-events-auto' }}
      />
    </div>
  );
}

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <QueryProvider>
      {children}
      <ToasterTopLayer />
    </QueryProvider>
  );
}
